import type { ListBuilderColumn } from '../../types';

/**
 * Image column - displays item primary image
 */
export const createImageColumn = (): ListBuilderColumn => ({
	id: 'image',
	width: 60,
	fields: [
		{
			id: 'item_image',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'primaryImageUrl',
					objectTable: 'client_shop',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				imageOptions: {
					size: 'small',
					enableViewer: true,
				},
			},
			displayStyle: 'image',
		},
	],
	headers: [
		{
			id: 'image_header',
			text: '',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Promo badge field — delegates to the `shop-promo-badge` custom component
 * registered by the clients module. The component renders the badge using
 * `promoBadgeText` / `promoHeadlineText` that the backend attaches via
 * `ClientItemService._enrichWithPromoBadges` and returns null when no
 * promo applies, so the cell collapses naturally on non-promo rows.
 *
 * This replaces the prior `displayStyle: 'badge'` + `field: 'promoBadgeText'`
 * config — that combination wasn't usable from the field configurator
 * dropdown because `promoBadgeText` is a post-processed (non-entity) field
 * that isn't surfaced in the field picker.
 */
const createPromoBadgeField = () => ({
	id: 'promo_badge',
	dataConfig: {
		type: 'field' as const,
		field: {
			fieldName: '_id',
			objectTable: 'client_shop',
		},
	},
	styleConfig: {
		size: 'sm' as const,
		colorRule: { type: 'default' as const },
	},
	displayStyle: 'custom' as const,
	customComponent: {
		id: 'shop-promo-badge',
	},
});

/**
 * Price-change indicator field — delegates to the `shop-price-change-indicator`
 * custom component. The component reads `_listPrice` / `previousPrice` /
 * `priceModifiedDatetime` / `lastOrderedListPrice` off the row data and
 * returns null when no change warrants a warning, so the cell stays empty
 * on rows with stable pricing.
 */
const createPriceChangeIndicatorField = () => ({
	id: 'price_change_indicator',
	dataConfig: {
		type: 'field' as const,
		field: {
			fieldName: '_id',
			objectTable: 'client_shop',
		},
	},
	styleConfig: {
		size: 'sm' as const,
		colorRule: { type: 'default' as const },
	},
	displayStyle: 'custom' as const,
	customComponent: {
		id: 'shop-price-change-indicator',
	},
});

/**
 * Price-contract indicator field — delegates to the
 * `shop-price-contract-indicator` custom component. The component reads
 * `_contractPriceBreaks` / `_listPrice` off the row data (populated by
 * `ClientShopListWrapper`) and returns null when no contract applies, so
 * the cell stays empty on rows without contract pricing.
 */
const createPriceContractIndicatorField = () => ({
	id: 'price_contract_indicator',
	dataConfig: {
		type: 'field' as const,
		field: {
			fieldName: '_id',
			objectTable: 'client_shop',
		},
	},
	styleConfig: {
		size: 'sm' as const,
		colorRule: { type: 'default' as const },
	},
	displayStyle: 'custom' as const,
	customComponent: {
		id: 'shop-price-contract-indicator',
	},
});

const createPromoBadgeLineBreakField = () => ({
	id: 'promo_badge_break',
	dataConfig: { type: 'custom_text' as const, customText: '' },
	styleConfig: { size: 'md' as const, colorRule: { type: 'default' as const } },
	displayStyle: 'line_break' as const,
	conditionalVisibility: {
		field: 'promoBadgeText',
		condition: 'is_not_null' as const,
	},
});

/**
 * Item details column - name with link and manufacturer (theme colors)
 */
export const createItemDetailsColumnTheme = (): ListBuilderColumn => ({
	id: 'item_details',
	minWidth: 150,
	fields: [
		createPromoBadgeField(),
		createPromoBadgeLineBreakField(),
		{
			id: 'description',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'item',
				},
				defaultValue: 'Unknown Item',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_desc',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'manufacturer',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'item_attribute_icons',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-item-attribute-icons',
			},
		},
	],
	headers: [
		{
			id: 'item_header',
			text: 'Name',
		},
		{
			id: 'header_1751545013388',
			text: 'Mfg',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Item details column - name with link and manufacturer (static colors, extra line break)
 */
export const createItemDetailsColumnStatic = (): ListBuilderColumn => ({
	id: 'item_details',
	minWidth: 150,
	fields: [
		createPromoBadgeField(),
		createPromoBadgeLineBreakField(),
		{
			id: 'description',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'item',
				},
				defaultValue: 'Unknown Item',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_desc',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'manufacturer',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'manufacturer',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'item_attribute_icons',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-item-attribute-icons',
			},
		},
	],
	headers: [
		{
			id: 'item_header',
			text: 'Name',
		},
		{
			id: 'header_1751545013388',
			text: 'Mfg',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Category column with conditional externalId/externalId2 (for shop_default)
 */
export const createCategoryColumnConditional = (): ListBuilderColumn => ({
	id: 'category',
	minWidth: 75,
	fields: [
		{
			id: 'field_1751545075287',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'externalId2',
								objectTable: 'item',
							},
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: {
						fieldName: 'externalId',
						objectTable: 'item',
					},
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545183916',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'category_field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'category',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1752779253753',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1752779256147',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'subcategory',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'header_1751545047862',
			text: 'Item #',
		},
		{
			id: 'category_header',
			text: 'Cat',
		},
		{
			id: 'header_1752779245678',
			text: 'Subcat',
		},
	],
	sortable: true,
	alignment: 'left',
	// Compound sort: items with externalId2 sort first by their clean id
	// (alphabetically), then any items without externalId2 (rare for HF
	// — placeholder rows with `UNMATCHED_…` only) sort to the end by
	// externalId as tiebreaker. Keeps the column sort consistent with
	// the conditional display rule above so what the user sees onscreen
	// is what determines sort order.
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'externalId2',
			objectTable: 'item',
			nullsLast: true,
		},
		{
			direction: 'ASC',
			fieldName: 'externalId',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Category column with conditional externalId/externalId2 (static colors,
 * extra line break). Display rule mirrors `createCategoryColumnConditional`:
 * prefer `externalId2` (the clean customer-visible id) over the raw
 * `externalId`, which can be a placeholder like `UNMATCHED_<source>_<id>`
 * for items that didn't match an external system reference. Without this
 * fallback, downstream consumers of this list config (e.g. the static-color
 * grid variant) would surface `UNMATCHED_…` and customers wouldn't
 * recognise their own items.
 */
export const createCategoryColumnStatic = (): ListBuilderColumn => ({
	id: 'category',
	minWidth: 75,
	fields: [
		{
			id: 'field_1751545075287',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'externalId2',
								objectTable: 'item',
							},
							defaultValue: '-',
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: {
						fieldName: 'externalId',
						objectTable: 'item',
					},
					defaultValue: '-',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545183916',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'category_field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'category',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1752779253753',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1752779256147',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'subcategory',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'header_1751545047862',
			text: 'Item #',
		},
		{
			id: 'category_header',
			text: 'Cat',
		},
		{
			id: 'header_1752779245678',
			text: 'Subcat',
		},
	],
	sortable: true,
	alignment: 'left',
	// Compound sort: items with externalId2 sort first by their clean id
	// (alphabetically), then any items without externalId2 (rare for HF
	// — placeholder rows with `UNMATCHED_…` only) sort to the end by
	// externalId as tiebreaker. Keeps the column sort consistent with
	// the conditional display rule above so what the user sees onscreen
	// is what determines sort order.
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'externalId2',
			objectTable: 'item',
			nullsLast: true,
		},
		{
			direction: 'ASC',
			fieldName: 'externalId',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Size column - sizeCount and unitOfMeasure (theme colors)
 */
export const createSizeColumnTheme = (): ListBuilderColumn => ({
	id: 'size',
	fields: [
		{
			id: 'price_prefix',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'sizeCount',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					decimalPlaces: 0,
				},
			},
			displayStyle: 'number',
		},
		{
			id: 'line_break_size',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'unit_of_measure',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'unitOfMeasure',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Size',
		},
		{
			id: 'unit_header',
			text: 'Unit',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'sizeCount',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Size column - sizeCount and unitOfMeasure (static colors, extra line break)
 */
export const createSizeColumnStatic = (): ListBuilderColumn => ({
	id: 'size',
	fields: [
		{
			id: 'price_prefix',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'sizeCount',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					decimalPlaces: 0,
				},
			},
			displayStyle: 'number',
		},
		{
			id: 'line_break_size',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'unit_of_measure',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'unitOfMeasure',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Size',
		},
		{
			id: 'unit_header',
			text: 'Unit',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'sizeCount',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Pricing column - price and minimumPrice (theme colors, sortable)
 */
export const createPricingColumnTheme = (): ListBuilderColumn => ({
	id: 'column_1751545591402',
	fields: [
		{
			id: 'field_1751545620039',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'price',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1751545636426',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545638994',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'minimumPrice',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'currency',
		},
		// Indicators sit on their OWN row underneath Target + Min so that
		// long prices ($1,234.56, $56,000.00, etc.) never wrap the icons
		// to an unpredictable position. The line_break above (between
		// Target and Min) forces Min onto row 2; this line_break forces
		// the indicators onto row 3. Both indicator components return
		// null when they have nothing to show, so this third row simply
		// collapses on rows that don't need either icon.
		{
			id: 'field_indicators_break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		createPriceChangeIndicatorField(),
		createPriceContractIndicatorField(),
	],
	headers: [
		{
			id: 'header_1751545591402',
			text: 'Target',
		},
		{
			id: 'header_1751545611857',
			text: 'Min.',
		},
	],
	sortable: true,
	sortFields: [
		{
			fieldName: 'price',
			objectTable: 'item',
			direction: 'DESC',
		},
	],
	alignment: 'right',
	verticalAlignment: 'top',
});

/**
 * Pricing column - price and minimumPrice (static colors, not sortable)
 */
export const createPricingColumnStatic = (): ListBuilderColumn => ({
	id: 'column_1751545591402',
	fields: [
		{
			id: 'field_1751545620039',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'price',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1751545636426',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545638994',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'minimumPrice',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		// Indicators on their own row beneath Target + Min (see Theme variant).
		{
			id: 'field_indicators_break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		createPriceChangeIndicatorField(),
		createPriceContractIndicatorField(),
	],
	headers: [
		{
			id: 'header_1751545591402',
			text: 'Target',
		},
		{
			id: 'header_1751545611857',
			text: 'Min.',
		},
	],
	sortable: false,
	alignment: 'right',
	verticalAlignment: 'top',
});

/**
 * Pricing column for purchased_together list (different field IDs)
 */
export const createPricingColumnPurchasedTogether = (): ListBuilderColumn => ({
	id: 'column_price',
	fields: [
		{
			id: 'field_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'price',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_lb_price',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_min_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'minimumPrice',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		// Indicators on their own row beneath Target + Min (see Theme variant).
		{
			id: 'field_indicators_break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		createPriceChangeIndicatorField(),
		createPriceContractIndicatorField(),
	],
	headers: [
		{
			id: 'header_target',
			text: 'Target',
		},
		{
			id: 'header_min',
			text: 'Min.',
		},
	],
	sortable: false,
	alignment: 'right',
	verticalAlignment: 'top',
});

/**
 * Stock column - quantityOnHand, quantityOnOrder, soonestExpirationDate (theme colors)
 */
export const createStockColumnTheme = (): ListBuilderColumn => ({
	id: 'stock',
	width: 100,
	fields: [
		{
			id: 'stock_status',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnHand',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-stock-status',
			},
		},
		{
			id: 'field_1751545326188',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545329661',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnOrder',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545336957',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545339632',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'stock_header',
			text: 'Available',
		},
		{
			id: 'header_1751545264070',
			text: 'On Order',
		},
		{
			id: 'header_1751545271709',
			text: 'Next Exp.',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Stock column - quantityOnHand, quantityOnOrder, soonestExpirationDate (static colors)
 */
export const createStockColumnStatic = (): ListBuilderColumn => ({
	id: 'stock',
	width: 100,
	fields: [
		{
			id: 'stock_status',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnHand',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-stock-status',
			},
		},
		{
			id: 'field_1751545326188',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545329661',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnOrder',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1751545336957',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751545339632',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'stock_header',
			text: 'Available',
		},
		{
			id: 'header_1751545264070',
			text: 'On Order',
		},
		{
			id: 'header_1751545271709',
			text: 'Next Exp.',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Stock column for purchased_together list (different field IDs)
 */
export const createStockColumnPurchasedTogether = (): ListBuilderColumn => ({
	id: 'stock',
	width: 100,
	fields: [
		{
			id: 'stock_status',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnHand',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'shop-stock-status',
			},
		},
		{
			id: 'field_lb_stock',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_on_order',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'quantityOnOrder',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_lb_order',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_expiration',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'soonestExpirationDate',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'stock_header',
			text: 'Available',
		},
		{
			id: 'header_on_order',
			text: 'On Order',
		},
		{
			id: 'header_exp',
			text: 'Next Exp.',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Last invoice data column - invoice link, last price, last date (theme colors)
 */
export const createLastInvoiceDataColumnTheme = (): ListBuilderColumn => ({
	id: 'last_invoice_data',
	minWidth: 90,
	fields: [
		{
			id: 'last_invoice_id',
			link: {
				type: 'object',
				enabled: true,
				idField: 'lastInvoiceId',
				objectTable: 'invoice',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceExternalId',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_invoice',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressLastPrice',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'line_break_price',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_invoice_date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceDate',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
				formatOptions: {
					dateFormat: 'medium',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'last_invoice_header',
			text: 'Last Inv',
		},
		{
			id: 'last_price_header',
			text: 'Last Price',
		},
		{
			id: 'last_date_header',
			text: 'Date',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'lastInvoiceDate',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Last invoice data column - invoice link, last price, last date (static colors)
 */
export const createLastInvoiceDataColumnStatic = (): ListBuilderColumn => ({
	id: 'last_invoice_data',
	minWidth: 90,
	fields: [
		{
			id: 'last_invoice_id',
			link: {
				type: 'object',
				enabled: true,
				idField: 'lastInvoiceId',
				objectTable: 'invoice',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceExternalId',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_invoice',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressLastPrice',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'line_break_price',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_invoice_date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastInvoiceDate',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					dateFormat: 'medium',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'last_invoice_header',
			text: 'Last Inv',
		},
		{
			id: 'last_price_header',
			text: 'Last Price',
		},
		{
			id: 'last_date_header',
			text: 'Date',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'lastInvoiceDate',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Sales data column - monthly units sold with dynamic month headers (theme colors)
 */
export const createSalesDataColumnTheme = (): ListBuilderColumn => ({
	id: 'sales_data',
	minWidth: 90,
	fields: [
		{
			id: 'current_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsMtd',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_current',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsLastMonth',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_last',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'two_months_ago',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsTwoMonthsAgo',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'current_month_header',
			text: '{{MONTH}}',
		},
		{
			id: 'last_month_header',
			text: '{{MONTH:-1m}}',
		},
		{
			id: 'two_months_ago_header',
			text: '{{MONTH:-2m}}',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'addressSoldUnitsMtd',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Sales data column - monthly units sold with dynamic month headers (static colors)
 */
export const createSalesDataColumnStatic = (): ListBuilderColumn => ({
	id: 'sales_data',
	minWidth: 90,
	fields: [
		{
			id: 'current_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsMtd',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_current',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'last_month',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsLastMonth',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line_break_last',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'two_months_ago',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressSoldUnitsTwoMonthsAgo',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'current_month_header',
			text: '{{MONTH}}',
		},
		{
			id: 'last_month_header',
			text: '{{MONTH:-1m}}',
		},
		{
			id: 'two_months_ago_header',
			text: '{{MONTH:-2m}}',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'addressSoldUnitsMtd',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Forecast column - forecasted reorder date and amount (only in shop_suggested)
 */
export const createForecastColumn = (): ListBuilderColumn => ({
	id: 'forecast',
	minWidth: 90,
	fields: [
		{
			id: 'forecast_date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressForecastedReorderDate',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'relative_date',
		},
		{
			id: 'line_break_forecast',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'forecast_amount',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'addressForecast',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{
			id: 'forecast_date_header',
			text: 'Reorder',
		},
		{
			id: 'forecast_amount_header',
			text: 'Forecast',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'addressForecastedReorderDate',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top',
});

/**
 * Actions column - custom component for shop actions
 */
export const createActionsColumn = (): ListBuilderColumn => ({
	id: 'actions',
	width: 220,
	fields: [
		{
			id: 'shop_actions',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: '_id',
					objectTable: 'client_shop',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'client-shop-actions-wrapper',
			},
		},
	],
	headers: [
		{
			id: 'actions_header',
			text: 'Qty / Price',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Category column for purchased_together list (different field IDs).
 * Display rule mirrors `createCategoryColumnConditional`: prefer
 * `externalId2` (clean) over `externalId` (which can be a placeholder
 * like `UNMATCHED_<source>_<id>` for items that didn't match an
 * external system reference).
 */
export const createCategoryColumnPurchasedTogether = (): ListBuilderColumn => ({
	id: 'category',
	minWidth: 75,
	fields: [
		{
			id: 'field_item_num',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'externalId2',
								objectTable: 'item',
							},
							defaultValue: '-',
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: {
						fieldName: 'externalId',
						objectTable: 'item',
					},
					defaultValue: '-',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_lb_item',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_category',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'category',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_lb_cat',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_subcategory',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'subcategory',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'header_item_num',
			text: 'Item #',
		},
		{
			id: 'category_header',
			text: 'Cat',
		},
		{
			id: 'header_subcat',
			text: 'Subcat',
		},
	],
	sortable: true,
	alignment: 'left',
	// Compound sort: items with externalId2 sort first by their clean id
	// (alphabetically), then any items without externalId2 (rare for HF
	// — placeholder rows with `UNMATCHED_…` only) sort to the end by
	// externalId as tiebreaker. Keeps the column sort consistent with
	// the conditional display rule above so what the user sees onscreen
	// is what determines sort order.
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'externalId2',
			objectTable: 'item',
			nullsLast: true,
		},
		{
			direction: 'ASC',
			fieldName: 'externalId',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Restocked date column — shown on the Recently Restocked tab. Renders the
 * date the item transitioned back into stock; sortable by lastRestockedDate.
 * The list's defaultSortId (`column-restocked_date-desc`) overrides the
 * direction below at runtime.
 */
export const createRestockedDateColumn = (): ListBuilderColumn => ({
	id: 'restocked_date',
	minWidth: 90,
	fields: [
		{
			id: 'restocked_date_field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastRestockedDate',
					objectTable: 'item',
				},
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'medium' },
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'restocked_date_header',
			text: 'Restocked',
		},
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'lastRestockedDate',
			objectTable: 'item',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top',
});

/**
 * Computed created date column — shown on the Newly Stocked tab. Prefers
 * externalCreatedDate (the date from the source ERP) and falls back to the
 * item record's _created_date when unset. Sort applies both fields in
 * order, mirroring how the conditional category column composes a sort
 * from its display rule. The list's defaultSortId
 * (`column-created_date-desc`) overrides the direction below at runtime.
 */
export const createComputedCreatedDateColumn = (): ListBuilderColumn => ({
	id: 'created_date',
	minWidth: 90,
	fields: [
		{
			id: 'created_date_field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'externalCreatedDate',
						operator: 'not_equals',
						value: null,
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'externalCreatedDate',
								objectTable: 'item',
							},
						},
					},
				],
				defaultDataConfig: {
					type: 'field',
					field: {
						fieldName: '_created_date',
						objectTable: 'item',
					},
					defaultValue: '-',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'medium' },
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'created_date_header',
			text: 'Added',
		},
	],
	sortable: true,
	alignment: 'right',
	// Multi-field sort acts like COALESCE(externalCreatedDate, _created_date):
	// items with externalCreatedDate sort by it first, the rest by
	// _created_date. Direction is overridden by the column-based defaultSortId.
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'externalCreatedDate',
			objectTable: 'item',
			nullsLast: true,
		},
		{
			direction: 'DESC',
			fieldName: '_created_date',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Standard columns for shop_default list
 */
export const createShopDefaultColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnTheme(),
	createCategoryColumnConditional(),
	createSizeColumnTheme(),
	createPricingColumnTheme(),
	createStockColumnTheme(),
	createLastInvoiceDataColumnTheme(),
	createSalesDataColumnTheme(),
	createActionsColumn(),
];

/**
 * Columns for shop_suggested (reorder) list - includes forecast column
 */
export const createShopSuggestedColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnStatic(),
	createCategoryColumnStatic(),
	createSizeColumnStatic(),
	createPricingColumnStatic(),
	createStockColumnStatic(),
	createLastInvoiceDataColumnStatic(),
	createSalesDataColumnStatic(),
	createForecastColumn(),
	createActionsColumn(),
];

/**
 * Standard columns for at_risk, previously_purchased, short_date lists
 */
export const createShopStandardColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnStatic(),
	createCategoryColumnStatic(),
	createSizeColumnStatic(),
	createPricingColumnStatic(),
	createStockColumnStatic(),
	createLastInvoiceDataColumnStatic(),
	createSalesDataColumnStatic(),
	createActionsColumn(),
];

/**
 * Columns for the Recently Restocked tab — same as the standard set with
 * the Restocked date column inserted before Actions, mirroring how the
 * Suggested tab inserts the forecast column.
 */
export const createShopRecentlyRestockedColumns =
	(): Array<ListBuilderColumn> => [
		createImageColumn(),
		createItemDetailsColumnStatic(),
		createCategoryColumnStatic(),
		createSizeColumnStatic(),
		createPricingColumnStatic(),
		createStockColumnStatic(),
		createLastInvoiceDataColumnStatic(),
		createSalesDataColumnStatic(),
		createRestockedDateColumn(),
		createActionsColumn(),
	];

/**
 * Columns for the Newly Stocked tab — same as the standard set with the
 * computed created date column inserted before Actions.
 */
export const createShopNewlyStockedColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnStatic(),
	createCategoryColumnStatic(),
	createSizeColumnStatic(),
	createPricingColumnStatic(),
	createStockColumnStatic(),
	createLastInvoiceDataColumnStatic(),
	createSalesDataColumnStatic(),
	createComputedCreatedDateColumn(),
	createActionsColumn(),
];

/**
 * Columns for purchased_together list (no invoice/sales data, different IDs)
 */
export const createShopPurchasedTogetherColumns =
	(): Array<ListBuilderColumn> => [
		createImageColumn(),
		createItemDetailsColumnStatic(),
		createCategoryColumnPurchasedTogether(),
		createSizeColumnStatic(),
		createPricingColumnPurchasedTogether(),
		createStockColumnPurchasedTogether(),
		createActionsColumn(),
	];
